import ExpandIcon from 'assets/svg/icons/ExpandIcon';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { AIAgentEnum, type Message } from 'types/ai.types';
import { NewLinkOutlineIcon, XIconOutline } from 'utils/icons';
import AIChat from '../AIChat/AIChat.component';
import { useAIChatContext } from '../AIChat/AIChat.context';
import AIChatTextbox from '../AIChat/AIChatTextbox';

type AIHelperChatProps = {
  initialMessage?: string;
  onClose: () => void;
  lastMessageActions?: React.ReactNode;
  renderMessage?: (message: Message) => JSX.Element;
  isExpandable?: boolean;
  hideRating?: boolean;
};

const AIHelperChat = ({
  initialMessage,
  lastMessageActions,
  onClose,
  renderMessage,
  isExpandable,
  hideRating,
}: AIHelperChatProps): JSX.Element => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const { conversationId, aiAgent } = useAIChatContext();

  const showLinkToChat = useMemo(
    () => conversationId && aiAgent !== AIAgentEnum.email_writer,
    [conversationId, aiAgent],
  );

  const navigate = useNavigate();
  const handleNavigateToChat = useCallback(
    () =>
      conversationId && navigate('/assistant?conversationId=' + conversationId),
    [navigate, conversationId],
  );

  return (
    <div
      className={classNames(
        'rounded-xl bg-white border border-primary-50 p-4 pt-0 shadow-lg transition-all duration-300 flex flex-col',
        {
          'w-[380px] min-h-[0px] max-h-[465px]': !isExpanded,
          'w-[30rem] min-h-[95vh] max-h-[95vh] top-[2.5vh]': isExpanded,
        },
      )}
    >
      <div className="flex flex-row items-center justify-between border-b-[1px] mb-4 border-primary-50">
        <div className="flex flex-row space-x-2 pt-3 pb-2 items-center">
          <div className="font-bold text-sm flex gap-2">
            <span className="font-degular !bg-clip-text ai-gradient !capitalize">
              {t('sidebar.support.ai.title')}
            </span>
          </div>
        </div>

        <div className="flex flex-row space-x-2">
          {isExpandable && (
            <button className="flex items-center justify-center w-6 h-6 rounded-md bg-primary-50 cursor-pointer">
              <ExpandIcon
                className="w-3 md:w-4 text-primary"
                onClick={() => setIsExpanded(!isExpanded)}
              />
            </button>
          )}

          {showLinkToChat && (
            <button className="flex items-center justify-center w-6 h-6 rounded-md bg-primary-50 cursor-pointer">
              <NewLinkOutlineIcon
                className="w-3 md:w-4 text-primary"
                onClick={handleNavigateToChat}
              />
            </button>
          )}

          <button className="flex items-center justify-center w-6 h-6 rounded-md bg-primary-50 cursor-pointer">
            <XIconOutline
              className="w-3 md:w-4 text-primary"
              onClick={onClose}
            />
          </button>
        </div>
      </div>

      <AIChat
        className={classNames(
          'flex flex-col space-y-3 overflow-auto px-2 pb-8 flex-1',
          { 'min-h-[180px]': !isExpanded },
        )}
        isSmall
        initialMessage={initialMessage}
        lastMessageActions={lastMessageActions}
        renderMessage={renderMessage}
        hideRating={hideRating}
      />

      <AIChatTextbox className="self-end" isSmall />
    </div>
  );
};

export default AIHelperChat;
